<i18n src="./translations"></i18n>

<template>
  <div :class="classes" :data-preload="[$options.name]">
    <div class="page-footer__first-row">
      <Newsletter
        v-if="!isReducedFooter"
        class="newsletter"
        subscription-src="footer"
        :subscription-url="
          frontFacingPath('customer.shop.ajax.newsletter.subscription')
        "
        :shop-name="shopName"
      />
      <Social
        v-if="!isDeshop && !isReducedFooter"
        :title="$t('page-footer.social')"
      />
    </div>

    <DeshopSocial
      v-if="isDeshop && !isReducedFooter"
      class="deshop-social"
      :title="$t('page-footer.social')"
    />

    <div v-if="!isReducedFooter" class="row links">
      <LinksList
        class="column one links__about"
        :title="navigation.about.headline"
        :items="navigation.about.links"
      >
        <p class="itemLink" @click="showCookiesMainBanner">
          <span data-v-cba9efb2 class="itemTitle"
            >{{ $t('page-footer.revisit_cookies') }}
          </span>
        </p>
      </LinksList>

      <LinksList
        class="column one links__services"
        :title="navigation.services.headline"
        :items="navigation.services.links"
      />

      <LinksList
        v-if="navigation.offers"
        class="column one links__offers"
        :title="navigation.offers.headline"
        :items="navigation.offers.links"
      />

      <FooterContactUs
        class="column two last links__contact-us"
        :title="contactUsHeadline"
        :phone="contactUsInfo.phone"
        :formatted-phone="contactUsInfo.formattedPhone"
        :opening-times="contactUsInfo.openingTimes"
        :email="contactUsInfo.email"
        :hide-phone-number="contactUsInfo.hideSupportPhoneNumber"
      />

      <LinksList
        v-if="isDeshop && navigation.cars"
        class="column one links__cars"
        :title="navigation.cars.headline"
        :items="navigation.cars.links"
      />

      <div
        v-if="!!paymentMethods.length || !!deliveryMethods.length"
        class="services"
      >
        <FooterServices
          v-if="!!paymentMethods.length"
          class="column three"
          type="payment"
          :title="$t('page-footer.accepted_payments')"
          :providers="paymentMethods"
        />

        <FooterServices
          v-show="showDeliveryMethods"
          class="column two"
          type="delivery"
          :title="$t('page-footer.shipping_providers')"
          :providers="deliveryMethods"
        />
      </div>

      <LinksList
        v-if="!isDeshop && navigation.cars"
        class="column five"
        columns="five"
        :title="navigation.cars.headline"
        :items="navigation.cars.links"
      />

      <LinksList
        v-if="navigation.products"
        class="column"
        columns="five"
        :title="navigation.products.headline"
        :items="navigation.products.links"
      />
    </div>

    <div class="row">
      <p v-if="!isReducedFooter" class="disclaimer">
        {{ $t('page-footer.disclaimer') }}
      </p>

      <div
        v-if="!isReducedFooter && navigation.channels"
        class="channelsWrapper"
      >
        <LinksList
          type="channels"
          :title="navigation.channels.headline"
          :items="navigation.channels.links"
        />
      </div>

      <div v-if="isReducedFooter" class="infoWrapper row">
        <div class="phoneContainer">
          <div class="phone">
            {{ $t('page-footer.have_questions') }}
            <a :href="`tel:${contactUsInfo.phone}`">{{
              contactUsInfo.formattedPhone
            }}</a>
          </div>

          <div class="times">
            {{ contactUsInfo.openingTimes }}
          </div>
        </div>

        <div class="links">
          <a
            v-for="(link, index) in navigation.about.links"
            :key="index"
            class="link"
            :href="link.url"
            >{{ link.title }}</a
          >
        </div>
      </div>

      <div class="copyrightWrapper row">
        <div class="copyright">
          {{ $t('page-footer.copyright', { '%url%': salesChannelUrl }) }}
        </div>
        <img
          class="logo"
          alt="Tec Alliance"
          :src="resolveFile('images/page-footer/tec_alliance.jpg')"
          loading="lazy"
          width="159"
          height="53"
        />
      </div>
    </div>
  </div>
</template>

<script>
import './images/tec_alliance.jpg';
import { mapGetters, mapMutations, mapState } from 'vuex';
import capitalize from 'lodash/capitalize';
import FooterContactUs from 'Components/02-molecules/footer-contact-us/FooterContactUs';
import FooterServices from 'Components/02-molecules/footer-services/FooterServices';
import LinksList from 'Components/02-molecules/links-list/LinksList';
import Social from 'Components/02-molecules/social/Social';
import DeshopSocial from 'Components/02-molecules/deshop-social/DeshopSocial';
import Newsletter from 'Components/02-molecules/newsletter/Newsletter';
import globalMixin from 'Libs/mixins/globalMixin';

export default {
  name: 'PageFooter',
  components: {
    FooterServices,
    LinksList,
    DeshopSocial,
    Social,
    FooterContactUs,
    Newsletter,
  },
  mixins: [globalMixin],
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState('core', {
      navigation: ({ navigation }) => navigation,
      companyId: (state) => state.requestData.companyId,
      shopName: ({ salesChannel }) =>
        salesChannel.displayName &&
        capitalize(salesChannel.displayName.replace('www.', '')),
      salesChannelUrl: ({ salesChannel }) => salesChannel.baseUrl,
      salesChannelConfig: ({ salesChannel }) => salesChannel.config,
      contactUsInfo() {
        return {
          email: this.isBusinessCustomer
            ? this.salesChannelConfig.b2BCustomerServiceEmail
            : this.salesChannelConfig.customerServiceEmail,
          phone: this.isBusinessCustomer
            ? this.salesChannelConfig.b2BCustomerServicePhoneLink
            : this.salesChannelConfig.customerServicePhoneLink,
          formattedPhone: this.isBusinessCustomer
            ? this.salesChannelConfig.b2BCustomerServicePhoneNumber
            : this.salesChannelConfig.customerServicePhoneNumber,
          openingTimes: this.isBusinessCustomer
            ? this.salesChannelConfig.b2BCustomerServiceOpeningHours
            : this.salesChannelConfig.customerServiceOpeningHours,
          hideSupportPhoneNumber:
            this.salesChannelConfig.hideSupportPhoneNumber || false,
        };
      },
      paymentMethods({ paymentMethods }) {
        const methods = paymentMethods.map((icon) => ({
          icon,
          title: this.$t(`page-footer.payment.${icon}`),
        }));

        return this.isBusinessCustomer ? [methods[0]] : methods;
      },
      deliveryMethods({ deliveryMethods }) {
        return deliveryMethods.map((icon) => ({
          icon,
          title: this.$t(`page-footer.delivery.${icon}`),
        }));
      },
      isDeshop({ salesChannel: { salesChannelId } }) {
        const DESHOP_SALES_CHANNEL_ID = 'www-kfzteile24-de';
        return salesChannelId === DESHOP_SALES_CHANNEL_ID;
      },
    }),
    ...mapGetters('core', {
      options: 'getLayoutParams',
      isBusinessCustomer: 'isBusinessCustomer',
    }),
    showDeliveryMethods() {
      return !!this.deliveryMethods.length && !this.isBusinessCustomer;
    },
    contactUsHeadline() {
      return this.contactUsInfo.hideSupportPhoneNumber
        ? this.$t('page-footer.hotline.customer_service')
        : this.$t('page-footer.hotline.headline');
    },
    isReducedFooter() {
      return this.isMobileSalesChannel || this.options.reducedFooter;
    },
  },
  methods: {
    ...mapMutations('cookiesMain', {
      openCookieBanner: 'OPEN_COOKIES_BANNER',
    }),
    showCookiesMainBanner() {
      this.openCookieBanner(true);
    },
  },
  serverCacheKey: ({ id = false }) => {
    /* istanbul ignore next */
    return id;
  },
};
</script>

<style scoped lang="scss">
@import 'variables/breakpoints';
@import 'mixins';

.pageFooter {
  margin-top: 64px;
  background: var(--color-white);
  text-align: center;
  min-height: 100px;
  border-top: 1px solid var(--color-alto);
  display: flex;
  flex-direction: column;

  @include page-size-padding(var(--space-2));

  @media #{$_mediaSDown} {
    @include page-size-padding(12px);
  }
}

.page-footer__first-row {
  display: flex;
}

.row {
  display: flex;
  flex-flow: column wrap;
  margin: 0 calc(-1 * var(--space-2));

  &:first-child {
    .column {
      margin-top: var(--space-2);
    }
  }
}

.social {
  align-items: flex-end;
}

.services {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.social,
.deshop-social,
.infoWrapper,
.links__contact-us,
.newsletter {
  padding: var(--space-2);
}

.disclaimer {
  max-width: 1000px;
  font-size: var(--font-size-S);
  color: var(--color-rolling-stone);
  line-height: 16px;
  text-align: left;
  padding: var(--space-2);
}

.copyrightWrapper {
  width: 100%;
  display: flex;
  padding: 0 var(--space-2);
}

.copyright {
  display: flex;
  align-items: center;
  flex-grow: 1;
  font-size: var(--font-size-SM);
  color: var(--color-rolling-stone);
  line-height: 20px;
}

.logo {
  width: min-content;
}

.infoWrapper {
  margin-bottom: var(--space-2);
  color: var(--color-rolling-stone);
  justify-content: space-between;
  padding-left: 30px;
  width: 100%;
}

.phoneContainer {
  text-align: left;
}

.phone {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 10px;
}

.times {
  font-size: var(--font-size-SM);
}

.links {
  margin-bottom: var(--space-2);
}

.link {
  margin-left: 8px;
}

@media #{$_mediaMDown} {
  .pageFooter {
    margin: 32px 0 0;
    padding: 0;
  }

  .page-footer__first-row {
    flex-direction: column;
  }

  .row {
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    &--justified {
      flex-direction: column;
    }
  }

  .column,
  .channelsWrapper {
    width: 100%;

    &.last {
      order: 999;
    }

    &--desktop {
      display: none;
    }
  }

  .channelsWrapper {
    order: -1;
  }

  .disclaimer {
    margin-bottom: 0;
  }

  .links__about {
    margin-top: 4px;
    border-top: 1px solid var(--color-alto);
  }

  .phoneContainer,
  .copyright {
    flex: 1 0 50%;
  }

  .links,
  .logo {
    flex: 1 0 50%;
  }

  .links {
    line-height: 1.3;
    margin-top: 13px;
  }

  .infoWrapper,
  .copyrightWrapper {
    padding-left: var(--space-2);
    margin-top: var(--space-2);
  }

  .copyrightWrapper {
    margin-bottom: var(--space-2);
  }

  .copyright {
    margin: var(--space-2) 0;
  }

  .newsletter {
    flex-direction: column;

    ::v-deep {
      .newsletter__message {
        margin-top: 8px;
        width: unset;
        max-width: unset;
        padding-left: unset;
      }
    }
  }
}

@media #{$_mediaSDown} {
  .social,
  .infoWrapper,
  .links__contact-us,
  .disclaimer,
  .newsletter {
    padding: 12px;
  }

  .links__contact-us {
    border-bottom: 1px solid var(--color-alto);
  }

  .links {
    text-align: left;
    margin-top: 20px;
    margin-bottom: 0;
  }

  .link {
    margin-right: 8px;
    margin-left: 0;
    display: inline-block;
    margin-bottom: 10px;
  }
}

@media #{$_mediaLUp} {
  .row {
    flex-direction: row;

    &--down {
      align-items: flex-end;
    }
  }

  .services {
    flex-direction: row;

    &--reverse {
      flex-direction: column-reverse;
    }
  }

  .mobile {
    display: none;
  }

  .column,
  .channelsWrapper {
    flex-grow: 1;
    flex-shrink: 0;
    width: 100%;
    margin-top: 25px;
    margin-bottom: 25px;
    padding: 0 var(--space-2);

    &.one {
      width: 16%;
    }

    &.two {
      width: 30%;
    }

    &.three {
      width: 60%;
    }
  }

  .logo,
  .copyright {
    margin: 0 var(--space-2) var(--space-2);
  }

  ::v-deep {
    .linksList .transitionExpand,
    .footerServices .transitionExpand {
      display: flex !important;
      height: auto !important;
      max-height: none !important;
    }
  }
}

@media #{$_mediaLDown} {
  .row,
  .infoWrapper {
    flex-direction: column;
    text-align: left;
  }

  .newsletter {
    width: 100%;
  }

  .social {
    align-items: flex-start;
  }

  .infoWrapper .links {
    text-align: left;
    margin-top: 13px;
    margin-left: 0;
    line-height: 1.3;
  }

  .link:first-child {
    margin-left: 0;
  }

  .link:last-child {
    margin-left: 0;
  }

  .copyrightWrapper {
    flex-direction: column-reverse;
  }
}
</style>
